/**
 * Module-level helpers for preparing dimmer device actions.
 */

import { convertUiPropertyToSceneCommand } from 'src/components/Scenes/helpers/utilities_ui_to_scenes';
import type {
	DeviceActionContext,
	PreparedDeviceAction,
	DimmerActionName,
	DimmerHandler,
} from 'src/components/Scenes/helpers/device_actions/types';
import type { DeviceAction } from 'src/types/DeviceActionUI';

/**
 * Applies on/off state changes for dimmer-capable devices.
 */
const handleTurn: DimmerHandler = (
	action: DeviceAction,
	context: DeviceActionContext,
	set: Record<string, unknown>,
) => {
	set[convertUiPropertyToSceneCommand('turn', context.channel)] = action.value;
};

/**
 * Applies brightness changes for dimmer-capable devices.
 */
const handleBrightness: DimmerHandler = (
	action: DeviceAction,
	context: DeviceActionContext,
	set: Record<string, unknown>,
) => {
	set[convertUiPropertyToSceneCommand('brightness', context.channel, 'white')] = action.value;
};

/**
 * Mapping of dimmer action names to their respective handlers.
 */
const DIMMER_ACTION_HANDLERS: Record<DimmerActionName, DimmerHandler> = {
	turn: handleTurn,
	state: handleTurn,
	brightness: handleBrightness,
	gain: handleBrightness,
	transition_duration: (action, context, set) => {
		set[convertUiPropertyToSceneCommand('transition_duration', context.channel)] = action.value;
	},
};

/**
 * List of supported action names that dimmer devices can process.
 */
/**
 * Exported list of supported dimmer action names for external reference.
 */
export const DIMMER_SUPPORTED_ACTIONS = Object.keys(DIMMER_ACTION_HANDLERS) as DimmerActionName[];

/**
 * Prepares action payload for dimmer devices.
 *
 * These are white-only lights (e.g., SHDM*, SHRGBW2 in white mode) that do not support RGB controls.
 *
 * @param context - Context containing shared helpers, channel metadata, and queued device actions.
 * Supported action names are listed in `DIMMER_SUPPORTED_ACTIONS`.
 * @returns Prepared action payload ready for dispatch to the device.
 */
export const prepareDimmerAction = (context: DeviceActionContext): PreparedDeviceAction => {
	const { hexToDecimal, actions } = context;

	const action: PreparedDeviceAction = {
		_gui_purpose: 'action',
		r: 'dev:' + hexToDecimal + ':setup',
	};

	const set: any = {
		_isdata: true,
	};

	for (const actionEl of actions) {
		const handler = DIMMER_ACTION_HANDLERS[actionEl.name as DimmerActionName];
		if (!handler) {
			continue;
		}
		handler(actionEl, context, set);
	}

	action.set = { ...set };

	return action;
};
